import React, { Fragment, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Fab,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { Visibility, ExpandLess, ExpandMore } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { StepProps } from '../../../components/_WMS/Stepper'
import {
  AsnLocateState,
  eStepAsnLocate,
  AsnReceptionSkuHistoryDto,
  WorkOrderReceptionInfoDto,
  WorkOrderReceptionDetailInfoDto,
  RelatedAsnSummary,
  WorkOrderReceptionDunInfoDto
} from '../types'
import { GenericInputAction } from '../../../components/UI/GenericInputAction/GenericInputAction'
import { PositionAttributeAlert } from '../../../components/UI/PositionAttributeAlert/PositionAttributeAlert'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { useScanKeyboard } from '../../../hooks/useScanKeyboard'
import { ClearAdornment } from '../../../components/UI/Adornments/ClearAdornment'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { NextAdornment } from '../../../components/UI/Adornments/NextAdornment'
import { useUser } from '../../../hooks/useUser'
import { useSettings } from '../../../hooks/useSettings'
import { eSetting } from '../../../enums/eSetting'
import { eTypeSetting } from '../../../enums/eTypeSetting'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Loading } from '../../../components/UI'
import { useModal } from '../../../hooks/useModal'
import { Modal } from '../../../components/_EOS/Modal'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { BatchTable } from '../../../components/_WMS/ListBatch/BatchTable'
import { Pagination } from '../../../components/Pagination'
import { usePagination } from '../../../hooks/usePagination'
import {
  getWorkOrder,
  checkPosition,
  validateSku,
  terminateLocateAsn,
  updateLpnState,
  getAsnHistory,
  getAsnSkuBatches
} from '../api'
import { ViewDetailStep } from './ViewDetailStep'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { eTypeStorage } from '../../../enums/eTypeStorage'
import { Batch } from '../../../interfaces/Batch'
import { ApiResponse } from '../../../typings/ApiResponse'
import { eBatchState } from '../../../enums/eBatch'
import { eTypePosition } from '../../../enums/eTypePosition'
import { eAsnState } from '../../../enums/eAsnState'
import { asnStoredStates } from '../../../enums/eAsnState'
import { RelatedAsnRow } from '../components/RelatedAsnRow'
import { getRelatedAsnPalette } from '../components/relatedAsnStyles'

type SkuViewMode = 'SKU' | 'EAN' | 'Nombre'

interface ScannedSku {
  skuId: number
  sku: string
  ean: string
  productName: string
  qty: number // SIEMPRE en unidades, incluso para filas escaneadas por DUN (ver dunUnitsPerPack)
  batchId?: number
  batchNumber?: string
  batchExpirationDate?: string | null
  withExcess: boolean
  // Presentes sólo cuando la fila se originó escaneando un código DUN. qty es siempre un
  // múltiplo exacto de dunUnitsPerPack por construcción (la clave de agrupación de addToList
  // incluye el propio dun), así que los packs se derivan como qty / dunUnitsPerPack.
  dun?: string
  dunUnitsPerPack?: number
  dunPackageName?: string
}

interface AddToListArgs {
  qty: number
  skuId: number
  sku: string
  ean: string
  productName: string
  pendingQty: number
  batchId?: number
  batchNumber?: string
  batchExpirationDate?: string | null
  dun?: string
  dunUnitsPerPack?: number
  dunPackageName?: string
}

/**
 * Movimiento retenido porque deja al SKU/lote por encima de las unidades
 * pendientes. Se guardan todos los datos del alta para poder aplicarla tal cual
 * si el operario confirma el sobrante.
 */
interface PendingExcess {
  qty: number
  skuId: number
  sku: string
  ean: string
  productName: string
  pendingQty: number
  alreadyEntered: number
  batchId?: number
  batchNumber?: string
  batchExpirationDate?: string | null
  dun?: string
  dunUnitsPerPack?: number
  dunPackageName?: string
}

interface LocateStepV2Props extends StepProps<AsnLocateState> {}

export const LocateStepV2: React.FC<LocateStepV2Props> = ({
  sharedState,
  setSharedState,
  goToStep,
  prev
}) => {
  const asn = sharedState.selectedResult ?? sharedState.results[0]
  const lpn = sharedState.selectedLpn ?? ''
  const hasLpn = !!lpn

  const {
    userDataState: { userData },
    getUserData
  } = useUser()
  const { settingsState, getSettings } = useSettings()
  const productOriginId = asn?.productOriginId ?? null
  const [unitaryPutaway, setUnitaryPutaway] = useState(false)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const confirmModal = useModal<string>('Confirmar Finalización')
  const backConfirmModal = useModal('¿Deseas salir?')
  const qualityModal = useModal('Estás moviendo a posición de calidad.')
  const batchModal = useModal('Lotes y Vencimientos')
  const relatedAsnModal = useModal('ASN relacionada detectada')  
  const finishRelatedAsnModal = useModal<RelatedAsnSummary[]>('Ubicación finalizada')
  const [completedBatchInfo, setCompletedBatchInfo] = useState<string | null>(
    null
  )
  const [pendingExcess, setPendingExcess] = useState<PendingExcess | null>(null)
  const [availableBatches, setAvailableBatches] = useState<Batch[]>([])
  const [batchSearch, setBatchSearch] = useState('')
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [pendingSkuId, setPendingSkuId] = useState<number | null>(null)
  const batchPagination = usePagination(5)
  const [isFlipped, setIsFlipped] = useState(false)

  const [workOrder, setWorkOrder] = useState<WorkOrderReceptionInfoDto | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [validatingPosition, setValidatingPosition] = useState(false)
  const [validatingSku, setValidatingSku] = useState(false)

  // Posición
  const [positionValue, setPositionValue] = useState('')
  const [positionId, setPositionId] = useState<number | null>(null)
  const [positionStatus, setPositionStatus] = useState<
    'default' | 'success' | 'error'
  >('default')
  const [positionError, setPositionError] = useState('')
  const [isQualityPosition, setIsQualityPosition] = useState(false)
  // Atributos de la posición (CheckPosition) para validar MaxCapacity/MonoSku/MonoBatch
  // de forma amigable antes de intentar guardar. currentSkuId/currentBatchId reflejan
  // quién ya ocupa la posición según BD (null si está limpia).
  const [positionMaxCapacity, setPositionMaxCapacity] = useState(0)
  const [positionMonoSku, setPositionMonoSku] = useState(false)
  const [positionMonoBatch, setPositionMonoBatch] = useState(false)
  const [positionCurrentStockQty, setPositionCurrentStockQty] = useState(0)
  const [positionCurrentSkuId, setPositionCurrentSkuId] = useState<
    number | null
  >(null)
  const [positionCurrentSku, setPositionCurrentSku] = useState<string | null>(
    null
  )
  const [positionCurrentBatchId, setPositionCurrentBatchId] = useState<
    number | null
  >(null)
  const [positionCurrentBatchNumber, setPositionCurrentBatchNumber] =
    useState<string | null>(null)

  // SKU
  const [skuValue, setSkuValue] = useState('')
  const [skuId, setSkuId] = useState<number | null>(null)
  const [skuStatus, setSkuStatus] = useState<'default' | 'success' | 'error'>(
    'default'
  )
  const [skuError, setSkuError] = useState('')
  const [skuName, setSkuName] = useState('')
  const [skuCode, setSkuCode] = useState('')
  const [skuEan, setSkuEan] = useState('')
  const [skuBatchId, setSkuBatchId] = useState<number | undefined>(undefined)
  const [skuBatchNumber, setSkuBatchNumber] = useState<string | undefined>(
    undefined
  )
  const [skuBatchExpiration, setSkuBatchExpiration] = useState<
    string | null | undefined
  >(undefined)
  const [skuPendingQty, setSkuPendingQty] = useState(0)

  // DUN del SKU actualmente seleccionado (si el escaneo se resolvió vía DUN)
  const [skuDun, setSkuDun] = useState<string | undefined>(undefined)
  const [skuDunUnitsPerPack, setSkuDunUnitsPerPack] = useState<
    number | undefined
  >(undefined)
  const [skuDunPackageName, setSkuDunPackageName] = useState<
    string | undefined
  >(undefined)

  // Cantidad
  const [qtyValue, setQtyValue] = useState('')
  const [qtyError, setQtyError] = useState('')

  // Lista acumulada en pantalla
  const [skuList, setSkuList] = useState<ScannedSku[]>([])
  const [viewMode, setViewMode] = useState<SkuViewMode>('SKU')

  // Historial
  const [history, setHistory] = useState<AsnReceptionSkuHistoryDto[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const [headerExpanded, setHeaderExpanded] = useState(true)

  useEffect(() => {
    if (skuList.length > 0) setHeaderExpanded(false)
  }, [skuList.length])

  const positionInputRef = useRef<HTMLInputElement>(null)
  const skuInputRef = useRef<HTMLInputElement>(null)
  const qtyInputRef = useRef<HTMLInputElement>(null)
  const batchSearchRef = useRef<HTMLInputElement>(null)

  /**
   * Combinaciones SKU+lote cuyo sobrante ya fue aceptado por el operario. La
   * unidad es siempre SKU+lote: los SKU sin lote entran igual, con lote vacio.
   *
   * Va en un ref y no en estado porque se consulta dentro de addToList en el
   * mismo tick del escaneo: con estado, dos lecturas seguidas leerian el valor
   * previo.
   */
  const excessAckedKeys = useRef<Set<string>>(new Set())

  const excessKey = (idSku: number, bId?: number) => `${idSku}|${bId ?? ''}`

  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  // La cantidad queda afuera: se escribe siempre a mano.
  const { autoFocus } = useScanKeyboard()

  useEffect(() => {
    if (!userData) getUserData()

    getWorkOrder(asn?.asnId ?? 0, lpn || undefined)
      .then(({ data }) => {
        const wo = data as WorkOrderReceptionInfoDto
        if (!wo) {
          openSnackBar('No se encontró la orden de trabajo', false)
          return
        }
        setWorkOrder(wo)
      })
      .catch(() => openSnackBar('Error al cargar la orden de trabajo', false))
      .finally(() => setLoading(false))

    setSharedState((prev) => ({ ...prev, hideBackButton: true }))
    return () => {
      setSharedState((prev) => ({ ...prev, hideBackButton: false }))
    }
  }, [])

  useEffect(() => {
    getSettings(eTypeSetting.Recepcion, productOriginId)
  }, [productOriginId])

  useEffect(() => {
    const cfg = settingsState.settings.find(
      (s) => s.settingsMetadataId === eSetting.InboundUnitByUnit
    )
    if (cfg) setUnitaryPutaway(cfg.value === 'true')
  }, [settingsState.settings])

  // Foco inicial en el campo de posición: arranca el flujo listo para escanear sin que el
  // operario tenga que tocar la pantalla. Una vez la posición se valida, el foco ya salta solo
  // a SKU (ver runValidatePosition) y vuelve ahí después de cada alta (ver commitToList).
  useEffect(() => {
    if (!loading && autoFocus && !positionId) {
      setTimeout(() => positionInputRef.current?.focus(), 50)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  // ── Cálculos de unidades ──
  // La cantidad tomaa de un detalle es la controlada; si no viene, toma la cantidad real
  const getDetailQty = (d: WorkOrderReceptionDetailInfoDto) =>
    d.controlledQty ?? d.realQty

  const lpnDetails =
    workOrder?.workOrderDetails.filter((d) => !hasLpn || d.lpn === lpn) ?? []
  const totalControlledQty = lpnDetails.reduce((a, d) => a + getDetailQty(d), 0)
  const alreadyLocatedQty = lpnDetails.reduce((a, d) => a + d.locatedQty, 0)
  const enteredOnScreen = skuList.reduce((a, s) => a + s.qty, 0)
  const calculatedInbound = alreadyLocatedQty + enteredOnScreen
  const pendingQty = Math.max(0, totalControlledQty - calculatedInbound)

  // ── Asn relacionadas (devolución de marca mixta) ──
  // Se toma de la work order y, si el back todavía no la informa ahí, del
  // resultado de búsqueda. Sin el dato no se renderiza nada.
  const relatedAsns = workOrder?.relatedAsns || asn?.relatedAsns || []
  const hasRelatedAsns = relatedAsns.length > 0 
  const singleRelatedAsn = relatedAsns.length === 1 ? relatedAsns[0] : undefined
  const singleRelatedAsnPalette = getRelatedAsnPalette(
    singleRelatedAsn?.asnStateId ?? 0
  )
  const relatedAsnNumbers = relatedAsns.map((a) => a.asnNumber).join(', ')
  // Nombra el pedido que genero la devolución
  const relatedAsnOrderId = workOrder?.externalOrderId
  // La ASN que se está ubicando más las relacioandas
  const totalAsnCount = relatedAsns.length + 1
  // Muestra ASN relacionadas pendientes
  const pendingRelatedAsns = (
    finishRelatedAsnModal.extendedProps || []
  ).filter((related) => !asnStoredStates.includes(related.asnStateId))

  // ── Posición ──
  const handleValidatePosition = (e: React.FormEvent) => {
    e.preventDefault()
    runValidatePosition(positionValue)
  }

  /**
   * Validacion parametrizada por valor. No lee del estado a proposito: asi puede
   * invocarse con un valor recien leido por la camara sin esperar el re-render.
   */
  const runValidatePosition = (rawValue: string) => {
    if (!rawValue.trim() || !workOrder) return
    setValidatingPosition(true)
    checkPosition(
      rawValue.trim(),
      workOrder.typeStorageId ?? eTypeStorage.Interno,
      workOrder.isReserveReception ?? false,
      workOrder.isPreSaleReception ?? false,
      workOrder.warehouseId,
      workOrder.typeOperationId ?? 0
    )
      .then(({ data }) => {
        // Capacidad ya ocupada por completo: avisar de inmediato al escanear, sin
        // esperar a que el operario llegue al campo cantidad (WMSE-4510).
        if (data.maxCapacity > 0 && data.currentStockQty >= data.maxCapacity) {
          setPositionStatus('error')
          setPositionError(
            `Capacidad máxima excedida.\nUbicación con capacidad máxima configurada: ${data.maxCapacity} unidades.`
          )
          return
        }

        const quality = data.typePositionId === eTypePosition.CalidadRecepcion
        setPositionId(data.positionId)
        setIsQualityPosition(quality)
        setPositionStatus('success')
        setPositionError('')
        setPositionMaxCapacity(data.maxCapacity)
        setPositionMonoSku(data.monoSku)
        setPositionMonoBatch(data.monoBatch)
        setPositionCurrentStockQty(data.currentStockQty)
        setPositionCurrentSkuId(data.currentSkuId)
        setPositionCurrentSku(data.currentSku)
        setPositionCurrentBatchId(data.currentBatchId)
        setPositionCurrentBatchNumber(data.currentBatchNumber)
        if (quality) qualityModal.openModal('sm')
        else if (autoFocus) setTimeout(() => skuInputRef.current?.focus(), 50)
      })
      .catch(({ response }) => {
        setPositionStatus('error')
        setPositionError(response?.data?.message ?? 'Posición no válida')
      })
      .finally(() => setValidatingPosition(false))
  }

  /**
   * Un solo boton de camara para los dos campos.
   *
   * El flujo es secuencial —handleValidateSku corta si no hay positionId, y al
   * validar la posicion el foco salta solo al SKU—, asi que el destino de la
   * lectura no es ambiguo: si todavia no hay posicion validada va a posicion, y
   * si ya la hay va al SKU.
   */
  const isScanningPosition = !positionId

  const handleCameraScan = (value: string) => {
    if (isScanningPosition) {
      setPositionValue(value)
      runValidatePosition(value)
      return
    }
    setSkuValue(value)
    runValidateSku(value)
  }

  /** Devuelve el foco al campo que corresponde al cerrar el dialogo sin leer. */
  const handleCameraCancel = () => {
    if (!autoFocus) return
    setTimeout(() => {
      if (isScanningPosition) positionInputRef.current?.focus()
      else skuInputRef.current?.focus()
    }, 0)
  }

  const handleClearPosition = () => {
    setPositionValue('')
    setPositionId(null)
    setPositionStatus('default')
    setPositionError('')
    setIsQualityPosition(false)
    setPositionMaxCapacity(0)
    setPositionMonoSku(false)
    setPositionMonoBatch(false)
    setPositionCurrentStockQty(0)
    setPositionCurrentSkuId(null)
    setPositionCurrentSku(null)
    setPositionCurrentBatchId(null)
    setPositionCurrentBatchNumber(null)
    handleClearSku()
  }

  // ── SKU ──
  const getDetailPendingQty = (skuIdVal: number, batchId?: number) => {
    const match = workOrder?.workOrderDetails.find(
      (d) =>
        d.skuId === skuIdVal &&
        (d.batchId ?? undefined) === (batchId ?? undefined) &&
        (!hasLpn || d.lpn === lpn)
    )
    return match ? getDetailQty(match) - match.locatedQty : 0
  }

  const handleValidateSku = (e: React.FormEvent) => {
    e.preventDefault()
    runValidateSku(skuValue)
  }

  /** Validacion parametrizada por valor, por el mismo motivo que la de posicion. */
  const runValidateSku = (rawValue: string) => {
    if (!rawValue.trim() || !positionId || !workOrder) return

    const trimmed = rawValue.trim()

    // Busca el SKU en los detalles de la work order por EAN o SKU
    let detail = workOrder.workOrderDetails.find(
      (d) => d.ean === trimmed || d.sku === trimmed
    )

    // Si no matcheó por SKU/EAN, intenta resolver el código como DUN (caja cerrada = N
    // unidades del SKU). workOrderDuns ya viene acotado a los SKUs de esta ASN.
    let matchedDun: WorkOrderReceptionDunInfoDto | undefined
    if (!detail) {
      matchedDun = workOrder.workOrderDuns?.find(
        (d) => d.dun.trim().toLowerCase() === trimmed.toLowerCase()
      )
      if (matchedDun) {
        detail = workOrder.workOrderDetails.find(
          (d) => d.skuId === matchedDun!.stockKeepingUnitId
        )
      }
    }

    if (!detail) {
      setSkuStatus('error')
      setSkuError('Código no encontrado en la orden (SKU/EAN/DUN)')
      return
    }

    if (matchedDun && matchedDun.quantity <= 0) {
      setSkuStatus('error')
      setSkuError(
        `El DUN ${matchedDun.dun} tiene una cantidad por empaque inválida`
      )
      return
    }

    // Binding const: conserva la narrowing de "no undefined" dentro de los closures async de abajo
    const resolvedDetail = detail
    const resolvedDun = matchedDun

    setValidatingSku(true)
    validateSku(resolvedDetail.skuId, positionId)
      .then(() => {
        setSkuId(resolvedDetail.skuId)
        setSkuName(resolvedDetail.skuName)
        setSkuCode(resolvedDetail.sku)
        setSkuEan(resolvedDetail.ean)
        setSkuBatchId(resolvedDetail.batchId)
        setSkuPendingQty(
          getDetailQty(resolvedDetail) - resolvedDetail.locatedQty
        )
        setSkuStatus('success')
        setSkuError('')
        setSkuDun(resolvedDun?.dun)
        setSkuDunUnitsPerPack(resolvedDun?.quantity)
        setSkuDunPackageName(
          resolvedDun?.typePackageName ?? (resolvedDun ? 'Pack' : undefined)
        )

        const focusOrAutoAdd = (
          bId?: number,
          bNum?: string,
          bExp?: string | null
        ) => {
          const pendingForBatch = getDetailPendingQty(resolvedDetail.skuId, bId)
          if (unitaryPutaway) {
            addToList({
              qty: resolvedDun ? resolvedDun.quantity : 1,
              skuId: resolvedDetail.skuId,
              sku: resolvedDetail.sku,
              ean: resolvedDetail.ean,
              productName: resolvedDetail.skuName,
              pendingQty: pendingForBatch,
              batchId: bId,
              batchNumber: bNum,
              batchExpirationDate: bExp,
              dun: resolvedDun?.dun,
              dunUnitsPerPack: resolvedDun?.quantity,
              dunPackageName:
                resolvedDun?.typePackageName ??
                (resolvedDun ? 'Pack' : undefined)
            })
          } else {
            setSkuPendingQty(pendingForBatch)
            setTimeout(() => qtyInputRef.current?.focus(), 50)
          }
        }

        // Solo busca lotes si el SKU tiene batchId
        if (resolvedDetail.batchId && workOrder) {
          getAsnSkuBatches(
            workOrder.asnId,
            resolvedDetail.skuId,
            lpn || undefined,
            undefined,
            [eBatchState.Created, eBatchState.Pending, eBatchState.Storing]
          )
            .then(({ data }) => {
              const response = data as ApiResponse<Batch>
              const batches = response.items ?? []
              if (batches.length === 1) {
                setSkuBatchId(batches[0].batchId)
                setSkuBatchNumber(batches[0].batchNumber)
                setSkuBatchExpiration(batches[0].expirationDate)
                focusOrAutoAdd(
                  batches[0].batchId,
                  batches[0].batchNumber,
                  batches[0].expirationDate
                )
              } else if (batches.length > 1) {
                setPendingSkuId(resolvedDetail.skuId)
                setAvailableBatches(batches)
                setBatchSearch('')
                batchPagination.reset()
                batchModal.openModal('sm')
              } else {
                focusOrAutoAdd()
              }
            })
            .catch(() => focusOrAutoAdd())
        } else {
          focusOrAutoAdd()
        }
      })
      .catch(({ response }) => {
        setSkuStatus('error')
        setSkuError(
          response?.data?.message ?? 'SKU no válido para esta posición'
        )
      })
      .finally(() => setValidatingSku(false))
  }

  const handleClearSku = () => {
    setSkuValue('')
    setSkuId(null)
    setSkuName('')
    setSkuCode('')
    setSkuEan('')
    setSkuBatchId(undefined)
    setSkuBatchNumber(undefined)
    setSkuBatchExpiration(undefined)
    setSkuStatus('default')
    setSkuError('')
    setQtyValue('')
    setQtyError('')
    setSkuDun(undefined)
    setSkuDunUnitsPerPack(undefined)
    setSkuDunPackageName(undefined)
  }

  // ── Cantidad ──
  /** Aplica el movimiento a la lista en pantalla. No valida sobrantes. */
  const commitToList = ({
    qty,
    skuId: idSku,
    sku: codeSku,
    ean: eanSku,
    productName: nameSku,
    pendingQty: pendingQtySku,
    batchId: bId,
    batchNumber: bNumber,
    batchExpirationDate: bExpiration,
    dun,
    dunUnitsPerPack,
    dunPackageName
  }: AddToListArgs) => {
    setSkuList((prev) => {
      // La clave de fila incluye el dun: sueltas y cada DUN distinto del mismo SKU quedan en
      // filas separadas (decisión de UX confirmada) — el backend igual consolida todo por SKU
      // al guardar, así que esto no afecta la integridad de los datos.
      const idx = prev.findIndex(
        (s) =>
          s.skuId === idSku &&
          s.batchId === bId &&
          (s.dun ?? null) === (dun ?? null)
      )

      let updated: ScannedSku[]
      if (idx >= 0) {
        updated = [...prev]
        updated[idx] = { ...updated[idx], qty: updated[idx].qty + qty }
      } else {
        updated = [
          ...prev,
          {
            skuId: idSku,
            sku: codeSku,
            ean: eanSku,
            productName: nameSku,
            qty,
            batchId: bId,
            batchNumber: bNumber,
            batchExpirationDate: bExpiration,
            withExcess: false,
            dun,
            dunUnitsPerPack,
            dunPackageName
          }
        ]
      }

      // El excedente/completado se agrega por (skuId, batchId) IGNORANDO el dun — sumando
      // todas las filas del grupo (sueltas + todos los DUN escaneados de ese SKU/lote) — y se
      // propaga el mismo resultado a todas las filas del grupo. Si se calculara por fila,
      // varias filas del mismo SKU podrían sumar más que lo pendiente sin que ninguna quedara
      // marcada como excedente.
      const sameGroupBefore = prev.filter(
        (s) => s.skuId === idSku && s.batchId === bId
      )
      const previousTotal = sameGroupBefore.reduce((a, s) => a + s.qty, 0)
      const newTotal = previousTotal + qty
      const groupExcess = newTotal > pendingQtySku
      const crossedThreshold =
        previousTotal < pendingQtySku && newTotal >= pendingQtySku

      if (crossedThreshold && !groupExcess && bId && bNumber) {
        setCompletedBatchInfo(`Lote: ${bNumber}`)
      }

      return updated.map((s) =>
        s.skuId === idSku && s.batchId === bId
          ? { ...s, withExcess: groupExcess }
          : s
      )
    })
    handleClearSku()
    if (autoFocus) setTimeout(() => skuInputRef.current?.focus(), 50)
  }

  /**
   * Reglas duras de la posición (MaxCapacity/MonoSku/MonoBatch): a diferencia del
   * excedente de pedido (ver abajo), estas NO admiten "confirmar de todas formas" —
   * se bloquean directo, porque el backend las rechazaría igual al guardar.
   *
   * Para MonoSku/MonoBatch, el "ocupante" a comparar es quien ya está en BD
   * (positionCurrentSkuId/positionCurrentBatchId), con fallback a lo ya cargado en
   * esta sesión (skuList) — cubre tanto stock previo real como una carga reciente
   * sin guardar aún en una posición que estaba limpia.
   */
  /**
   * Los mensajes de atributos de posición (bloqueo, monosku, monolote, capacidad
   * máxima — WMSE-4510) se muestran en PositionAttributeAlert en vez del errorText
   * plano de GenericInputAction. No hay un código de error estructurado que llegue
   * desde el backend, así que se distinguen por el prefijo del mensaje.
   */
  const isPositionAttributeMessage = (msg: string) =>
    msg.startsWith('Ubicación bloqueada') ||
    msg.startsWith('Ubicación monosku') ||
    msg.startsWith('Ubicación monolote') ||
    msg.startsWith('Capacidad máxima excedida')

  const getPositionAttributeError = (
    idSku: number,
    bId: number | undefined,
    qty: number
  ): string | null => {
    if (positionMonoSku) {
      const occupantId = positionCurrentSkuId ?? skuList[0]?.skuId
      if (occupantId != null && occupantId !== idSku) {
        const occupantLabel =
          positionCurrentSkuId != null ? positionCurrentSku : skuList[0]?.sku
        return occupantLabel
          ? `Ubicación monosku. Solo permite el SKU: ${occupantLabel}.`
          : 'Ubicación monosku. Solo permite un SKU en esta posición.'
      }
    }

    if (positionMonoBatch) {
      const occupantId = positionCurrentBatchId ?? skuList[0]?.batchId
      if (occupantId != null && occupantId !== bId) {
        const occupantLabel =
          positionCurrentBatchId != null
            ? positionCurrentBatchNumber
            : skuList[0]?.batchNumber
        return occupantLabel
          ? `Ubicación monolote. Solo permite el lote: ${occupantLabel}.`
          : 'Ubicación monolote. Solo permite un lote en esta posición.'
      }
    }

    if (positionMaxCapacity > 0) {
      const alreadyOnScreen = skuList.reduce((a, s) => a + s.qty, 0)
      const total = positionCurrentStockQty + alreadyOnScreen + qty
      if (total > positionMaxCapacity) {
        return `Capacidad máxima excedida.\nUbicación con capacidad máxima configurada: ${positionMaxCapacity} unidades.`
      }
    }

    return null
  }

  /**
   * Toda alta pasa por aca. Si el movimiento deja al SKU por encima de las
   * unidades pendientes no se aplica: queda retenido hasta que el operario
   * confirme el sobrante en el modal.
   *
   * El aviso se levanta una sola vez por SKU+lote: una vez aceptado el
   * sobrante, los ingresos siguientes de esa combinacion se aplican directo
   * (ver excessAckedKeys).
   *
   * "Ya ingresado" se calcula sumando TODAS las filas del grupo (skuId,
   * batchId) sin importar el dun: con DUN puede haber varias filas del mismo
   * SKU+lote (sueltas + distintos DUN) y quedarse con la primera (.find)
   * subestimaria el total ya cargado, dejando pasar sobrantes sin confirmar.
   */
  const addToList = (args: AddToListArgs) => {
    const { qty, skuId: idSku, batchId: bId, pendingQty: pendingQtySku } = args

    const attributeError = getPositionAttributeError(idSku, bId, qty)
    if (attributeError) {
      if (unitaryPutaway) {
        setSkuStatus('error')
        setSkuError(attributeError)
      } else {
        setQtyError(attributeError)
      }
      return
    }

    const alreadyEntered = skuList
      .filter((s) => s.skuId === idSku && s.batchId === bId)
      .reduce((a, s) => a + s.qty, 0)

    if (
      alreadyEntered + qty > pendingQtySku &&
      !excessAckedKeys.current.has(excessKey(idSku, bId))
    ) {
      setPendingExcess({
        qty: args.qty,
        skuId: args.skuId,
        sku: args.sku,
        ean: args.ean,
        productName: args.productName,
        pendingQty: args.pendingQty,
        alreadyEntered,
        batchId: args.batchId,
        batchNumber: args.batchNumber,
        batchExpirationDate: args.batchExpirationDate,
        dun: args.dun,
        dunUnitsPerPack: args.dunUnitsPerPack,
        dunPackageName: args.dunPackageName
      })
      return
    }

    commitToList(args)
  }

  const handleConfirmExcess = () => {
    if (!pendingExcess) return
    const e = pendingExcess
    excessAckedKeys.current.add(excessKey(e.skuId, e.batchId))
    setPendingExcess(null)
    commitToList({
      qty: e.qty,
      skuId: e.skuId,
      sku: e.sku,
      ean: e.ean,
      productName: e.productName,
      pendingQty: e.pendingQty,
      batchId: e.batchId,
      batchNumber: e.batchNumber,
      batchExpirationDate: e.batchExpirationDate,
      dun: e.dun,
      dunUnitsPerPack: e.dunUnitsPerPack,
      dunPackageName: e.dunPackageName
    })
  }

  /** Cancelar descarta el movimiento completo: no se suma nada a la lista. */
  const handleCancelExcess = () => {
    setPendingExcess(null)
    handleClearSku()
    if (autoFocus) setTimeout(() => skuInputRef.current?.focus(), 100)
  }

  const handleAddQty = () => {
    const enteredQty = parseInt(qtyValue)
    if (!enteredQty || enteredQty <= 0 || !skuId) return
    // Si el SKU activo vino de un DUN, el número tecleado es cantidad de PACKS, no unidades
    const qty =
      skuDun && skuDunUnitsPerPack
        ? enteredQty * skuDunUnitsPerPack
        : enteredQty
    addToList({
      qty,
      skuId,
      sku: skuCode,
      ean: skuEan,
      productName: skuName,
      pendingQty: skuPendingQty,
      batchId: skuBatchId,
      batchNumber: skuBatchNumber,
      batchExpirationDate: skuBatchExpiration,
      dun: skuDun,
      dunUnitsPerPack: skuDunUnitsPerPack,
      dunPackageName: skuDunPackageName
    })
  }

  const handleShowAllBatches = () => {
    if (!workOrder || !pendingSkuId) return
    setLoadingBatches(true)
    setBatchSearch('')
    batchPagination.reset()
    getAsnSkuBatches(
      workOrder.asnId,
      pendingSkuId,
      lpn || undefined,
      undefined,
      undefined,
      500
    )
      .then(({ data }) => {
        const response = data as ApiResponse<Batch>
        setAvailableBatches(response.items ?? [])
      })
      .catch(() => openSnackBar('Error al cargar lotes', false))
      .finally(() => setLoadingBatches(false))
  }

  const handleBatchSelect = (batch: Batch) => {
    setSkuBatchId(batch.batchId)
    setSkuBatchNumber(batch.batchNumber)
    setSkuBatchExpiration(batch.expirationDate)
    const pendingForBatch = getDetailPendingQty(skuId!, batch.batchId)
    setSkuPendingQty(pendingForBatch)
    batchModal.closeModal()
    if (unitaryPutaway) {
      addToList({
        qty: skuDun && skuDunUnitsPerPack ? skuDunUnitsPerPack : 1,
        skuId: skuId!,
        sku: skuCode,
        ean: skuEan,
        productName: skuName,
        pendingQty: pendingForBatch,
        batchId: batch.batchId,
        batchNumber: batch.batchNumber,
        batchExpirationDate: batch.expirationDate,
        dun: skuDun,
        dunUnitsPerPack: skuDunUnitsPerPack,
        dunPackageName: skuDunPackageName
      })
    } else {
      setTimeout(() => qtyInputRef.current?.focus(), 50)
    }
  }

  /**
   * Filtra por el lote leido y, si coincide exacto con uno solo, lo selecciona:
   * es el equivalente al Enter del lector fisico. Con coincidencia parcial se
   * deja el filtro aplicado para que el operario elija de la lista.
   */
  const handleBatchCameraScan = (value: string) => {
    const scanned = value.trim()
    setBatchSearch(scanned)
    // Sin esto la lista queda vacia si el operario estaba en una pagina > 0.
    batchPagination.setPagination(0, batchPagination.pageSize)

    const exact = availableBatches.filter(
      (b) => b.batchNumber?.toLowerCase() === scanned.toLowerCase()
    )
    if (exact.length === 1) handleBatchSelect(exact[0])
  }

  const handleBatchSearchChange = (value: string) => {
    setBatchSearch(value)
    batchPagination.setPagination(0, batchPagination.pageSize)
  }

  const filteredBatches = batchSearch.trim()
    ? availableBatches.filter(
        (b) =>
          b.batchNumber?.toLowerCase().includes(batchSearch.toLowerCase()) ||
          b.expirationDate?.includes(batchSearch)
      )
    : availableBatches

  const startIndex = batchPagination.page * batchPagination.pageSize
  const paginatedBatches = filteredBatches.slice(
    startIndex,
    startIndex + batchPagination.pageSize
  )

  const handleBatchPaginationChange = (page: number, pageSize: number) => {
    const auxPage = batchPagination.pageSize !== pageSize ? 0 : page
    batchPagination.setPagination(auxPage, pageSize)
  }

  const handleRemoveItem = (
    skuIdVal: number,
    batchId?: number,
    dun?: string
  ) => {
    setSkuList((prev) =>
      prev.filter(
        (s) =>
          !(
            s.skuId === skuIdVal &&
            s.batchId === batchId &&
            (s.dun ?? null) === (dun ?? null)
          )
      )
    )
  }

  const hasExcess = skuList.some((s) => s.withExcess)

  const handleOpenDetail = () => {
    if (!workOrder) return
    setIsFlipped(true)
    setLoadingHistory(true)
    getAsnHistory(workOrder.asnId, workOrder.workOrderId, lpn || undefined)
      .then(({ data }) => setHistory(data ?? []))
      .catch(() => openSnackBar('Error al cargar el historial', false))
      .finally(() => setLoadingHistory(false))
  }

  // ── Payload hacia TerminateLocateASN ──
  const buildAsnLocatedSkusDto = () =>
    skuList.map((s) => {
      // Si la fila vino de un DUN y qty sigue siendo múltiplo exacto de su factor (invariante
      // esperada), se manda el código DUN + la cantidad de packs; el backend resuelve y valida
      // que el SKU coincida. Cualquier inconsistencia degrada a mandar unidades sueltas — nunca
      // corrompe la cantidad, en el peor caso se pierde la trazabilidad de que era un pack.
      if (
        s.dun &&
        s.dunUnitsPerPack &&
        s.dunUnitsPerPack > 0 &&
        s.qty % s.dunUnitsPerPack === 0
      ) {
        return {
          skuId: s.skuId,
          locatedQty: s.qty / s.dunUnitsPerPack,
          batchId: s.batchId,
          dun: s.dun
        }
      }
      return { skuId: s.skuId, locatedQty: s.qty, batchId: s.batchId }
    })

  // ── Guardar ──
  /**
   * Cierre del flujo: la ASN ya no tiene nada pendiente, asi que no hay LPN ni
   * resultado al que volver. Se avisa y se vuelve al escaneo (paso uno).
   *
   * `finishing` mantiene el <Loading /> hasta el salto. Sin eso, al bajar
   * `saving` el operario ve un instante la pantalla del putaway ya vaciada
   * —sin posicion ni lista— antes de que cambie el paso.
   */
  const finishToScanStep = (asnNumber: string) => {
    setFinishing(true)
    openSnackBar(`ASN ${asnNumber} finalizada`, true)
    setTimeout(() => goToStep(eStepAsnLocate.ScanStep), 1500)
  }

  /**
   * La LPN actual quedo sin unidades pendientes pero la ASN sigue abierta: no
   * hay nada mas que ubicar aca, se vuelve a la seleccion de LPN para tomar la
   * siguiente. Solo aplica a ASN con LPN.
   */
  const backToLpnStep = () => {
    setFinishing(true)
    openSnackBar(`LPN ${lpn} sin unidades pendientes`, true)
    setTimeout(() => prev(), 1500)
  }

  const handleSave = () => {
    if (!positionId || !userData?.userId || !workOrder || skuList.length === 0)
      return
    setSaving(true)

    terminateLocateAsn({
      workOrderId: workOrder.workOrderId,
      asnId: workOrder.asnId,
      lpn,
      positionId,
      userId: userData.userId,
      asnLocatedSkusDto: buildAsnLocatedSkusDto()
    })
      .then(({ data }) => {
        const wo = data as WorkOrderReceptionInfoDto
        setWorkOrder(wo)
        setSkuList([])
        handleClearPosition()

        // ASN cerrada: se sale del flujo. Lo decide el estado del backend, no los
        // detalles pendientes —que son de la LPN en curso, no de la ASN entera.
        if (wo.asnStateId === eAsnState.Finished) {
          finishToScanStep(wo.asnNumber)
          return
        }

        // ASN abierta pero sin nada mas que ubicar en esta LPN: se vuelve a la
        // seleccion de LPN.
        const lpnRemaining = wo.workOrderDetails.filter(
          (d) => d.lpn === lpn && getDetailQty(d) - d.locatedQty > 0
        )
        if (hasLpn && lpnRemaining.length === 0) {        
          const woRelatedAsns = wo.relatedAsns || relatedAsns

          if (woRelatedAsns.length > 0) {            
            // Se retiene al operario para mostrar las Asn pendientes 
            // de almacenar relacioandas
            finishRelatedAsnModal.openModal('xs', woRelatedAsns)
          } else {
            backToLpnStep()
            return
          }
        }

        openSnackBar('Ubicación guardada', true)
      })
      .catch(({ response }) => {
        openSnackBar(response?.data?.message ?? 'Error al guardar', false)
      })
      .finally(() => setSaving(false))
  }

  // ── Finalizar LPN ──
  const handleFinalizeLpn = () => {
    if (!workOrder) return
    setSaving(true)

    /**
     * AsnUpdateStateLpn no devuelve nada, asi que por esta via no hay forma de
     * saber si la LPN finalizada cerro tambien la ASN: siempre se vuelve al
     * listado de LPNs. Queda pendiente unificar en TerminateLocateASN con
     * lpnStateId/finishLPN —como hacia el putaway legacy—, que si retorna el
     * estado de la ASN.
     */
    const doFinalize = () =>
      updateLpnState(workOrder.asnId, lpn)
        .then(() => {
          setFinishing(true)
          openSnackBar(`LPN ${lpn} finalizada`, true)
          setTimeout(() => prev(), 1500)
        })
        .catch(({ response }) => {
          openSnackBar(
            response?.data?.message ?? 'Error al finalizar LPN',
            false
          )
        })
        .finally(() => setSaving(false))

    if (skuList.length > 0 && positionId && userData?.userId) {
      terminateLocateAsn({
        workOrderId: workOrder.workOrderId,
        asnId: workOrder.asnId,
        lpn,
        positionId,
        userId: userData.userId,
        asnLocatedSkusDto: buildAsnLocatedSkusDto()
      })
        .then(({ data }) => {
          const wo = data as WorkOrderReceptionInfoDto
          // Este ultimo guardado puede cerrar la ASN completa. Si vuelve
          // finalizada no queda LPN que finalizar ni paso al que volver: se
          // corta aca y se vuelve al escaneo.
          if (wo?.asnStateId === eAsnState.Finished) {
            finishToScanStep(wo.asnNumber)
            return
          }
          doFinalize()
        })
        .catch(({ response }) => {
          openSnackBar(response?.data?.message ?? 'Error al guardar', false)
          setSaving(false)
        })
    } else {
      doFinalize()
    }
  }

  // ── Header ──
  const createdDate = workOrder?.asnCreatedDate
    ? new Date(workOrder.asnCreatedDate).toLocaleDateString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : '—'

  const chipFields = hasLpn
    ? [
        { label: 'ASN', value: workOrder?.asnNumber ?? '—' },
        { label: 'LPN', value: lpn },
        { label: 'Marca', value: workOrder?.brandName ?? '—' },
        { label: 'Creada', value: createdDate }
      ]
    : [
        { label: 'ASN', value: workOrder?.asnNumber ?? '—' },
        { label: 'Marca', value: workOrder?.brandName ?? '—' }
      ]

  const isLong = (val: string) => val.length > 15

  const getPositionPlaceholder = () => {
    if (workOrder?.isReserveReception) return 'Escanea la ubicación (RESERVA)'
    if (workOrder?.isPreSaleReception)
      return 'Escanea la ubicación (VENTA EN VERDE)'
    return 'Escanea la ubicación'
  }

  const getQtyPlaceholder = () =>
    skuDun && skuDunUnitsPerPack
      ? `Cantidad de ${skuDunPackageName ?? 'Pack'} (x${skuDunUnitsPerPack} uds)`
      : 'Ingresa cantidad'

  // El snackbar se renderiza junto al Loading para que el aviso de cierre sea
  // visible mientras se espera el salto de paso.
  if (loading || saving || finishing || validatingPosition || validatingSku)
    return (
      <Fragment>
        <Loading />
        <SnackBar
          message={message}
          open={isActive}
          status={isSuccess ? 'success' : 'error'}
        />
      </Fragment>
    )

  // ── Vista historial ──
  if (isFlipped) {
    return (
      <ViewDetailStep
        asnNumber={workOrder?.asnNumber ?? ''}
        lpn={lpn}
        hasLpn={hasLpn}
        history={history}
        loading={loadingHistory}
        onClose={() => setIsFlipped(false)}
      />
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '0 4px' }}>
      <div style={{ padding: '12px 16px 4px 16px', flexShrink: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 500, color: '#333' }}>
          {positionStatus !== 'success'
            ? 'Escanea o escribe una ubicación'
            : skuStatus !== 'success'
              ? 'Ingresa o selecciona un SKU/EAN/DUN'
              : 'Ingresa la cantidad'}
        </Typography>
      </div>

      {/* HEADER CARD */}
      <Box
        sx={{
          width: '100%',
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          backgroundColor: 'background.paper'
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: '6px',
            minHeight: '48px'
          }}
        >
          <Fab
            variant='extended'
            size='medium'
            onClick={() =>
              skuList.length > 0 ? backConfirmModal.openModal('xs') : prev()
            }
            style={{
              borderRadius: '12px',
              height: '40px',
              paddingLeft: '10px',
              paddingRight: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #E0E0E0',
              boxShadow: 'none',
              gap: '6px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#666',
              textTransform: 'none'
            }}
          >
            <ArrowBackIcon style={{ fontSize: 18, color: '#1976d2' }} />
            Volver
          </Fab>
          <Fab
            variant='extended'
            size='medium'
            onClick={handleOpenDetail}
            style={{
              borderRadius: '12px',
              height: '40px',
              paddingLeft: '10px',
              paddingRight: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #E0E0E0',
              boxShadow: 'none',
              gap: '6px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#666',
              textTransform: 'none'
            }}
          >
            <Visibility style={{ fontSize: 18, color: '#1976d2' }} />
            Mostrar Ubicados
          </Fab>
        </Box>

        {/* Chips expandibles */}
        <Collapse in={headerExpanded}>
          {workOrder?.isPreSaleReception && (
            <Box sx={{ px: 1.5, pt: 0.75 }}>
              <Typography
                variant='body2'
                sx={{ color: 'rgb(0, 75, 177)', fontWeight: 500 }}
              >
                Recepción para venta en verde.
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              px: 1.5,
              py: '10px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '6px',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            {chipFields.map(({ label, value }) => (
              <Box
                key={label}
                sx={{ gridColumn: isLong(value) ? 'span 2' : 'span 1' }}
              >
                <Chip
                  label={`${label}: ${value}`}
                  color={hasLpn ? 'primary' : 'secondary'}
                  variant='outlined'
                  style={{ fontSize: '0.8125rem', width: '100%' }}
                />
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Muestra infromaciónde las Asn relacioandas */}
        {hasRelatedAsns && (
          <Box
            onClick={() => relatedAsnModal.openModal('xs')}
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#fff3e0',
              cursor: 'pointer'
            }}
          >
            <WarningAmberIcon
              sx={{ color: '#ff9800', fontSize: 20, flexShrink: 0, mt: '2px' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='body2'
                sx={{ color: '#e65100', fontWeight: 500 }}
              >
                {relatedAsnOrderId ? (
                  <Fragment>
                    Para la devolución del pedido{' '}
                    <strong>{relatedAsnOrderId}</strong>{' '}
                  </Fragment>
                ) : (
                  'Para esta devolución '
                )}
                existen ASN relacionadas:{' '}                
                <Box
                  component='span'
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {relatedAsnNumbers}
                </Box>
              </Typography>
              {singleRelatedAsn && (
                <Chip
                  label={`Estado: ${singleRelatedAsn.asnStateName}`}
                  size='small'
                  sx={{
                    mt: 0.75,
                    height: 22,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: singleRelatedAsnPalette.text,                    
                    backgroundColor: '#fff',
                    border: `1px solid ${singleRelatedAsnPalette.border}`
                  }}
                />
              )}
              <Typography
                variant='caption'
                sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
              >
                Toca para más detalles
              </Typography>
            </Box>
          </Box>
        )}

        {/* Fila Ver más + alerta sobrantes */}
        <Box
          sx={{
            px: 1.5,
            py: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          {hasExcess ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningAmberIcon sx={{ color: 'error.main', fontSize: 16 }} />
              <Typography
                variant='body2'
                sx={{ color: 'error.main', fontWeight: 600 }}
              >
                Sobrantes Ingresados.
              </Typography>
            </Box>
          ) : (
            <Box />
          )}
          <Button
            size='small'
            variant='text'
            onClick={() => setHeaderExpanded((v) => !v)}
            endIcon={
              headerExpanded ? (
                <ExpandLess fontSize='small' />
              ) : (
                <ExpandMore fontSize='small' />
              )
            }
            sx={{
              textTransform: 'none',
              color: '#1976d2',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            {headerExpanded ? 'Ver menos' : 'Ver más'}
          </Button>
        </Box>

        {/* Stats siempre visibles */}
        <Box
          sx={{
            px: 1.5,
            py: '8px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px'
          }}
        >
          {[
            { label: 'Pendientes', value: pendingQty, color: '#3B6D11' },
            { label: 'Ingresadas', value: calculatedInbound, color: '#185FA5' },
            {
              label: 'Uds. Totales',
              value: totalControlledQty,
              color: 'text.primary'
            }
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                p: '6px 8px',
                textAlign: 'center'
              }}
            >
              <Typography
                sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.3 }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 18, fontWeight: 500, color, lineHeight: 1.2 }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* INPUTS */}
      <Box sx={{ display: 'flex', flexDirection: 'column', px: '3px' }}>
        <form onSubmit={handleValidatePosition}>
          <GenericInputAction
            name='position'
            id='position'
            inputRef={positionInputRef}
            placeholder={getPositionPlaceholder()}
            value={positionValue}
            onChange={(e) => {
              setPositionValue(e.target.value)
              setPositionStatus('default')
            }}
            status={positionStatus}
            error={positionStatus === 'error' && !isPositionAttributeMessage(positionError)}
            errorText={
              isPositionAttributeMessage(positionError) ? '' : positionError
            }
            backgroundColor={isQualityPosition ? '#ffd39e' : undefined}
            adornment={
              positionStatus === 'default' ? (
                <ScanIcon width={28} height={36} />
              ) : (
                <ClearAdornment
                  onClick={handleClearPosition}
                  disabled={skuList.length > 0}
                />
              )
            }
          />
          {positionStatus === 'error' && isPositionAttributeMessage(positionError) && (
            <PositionAttributeAlert message={positionError} variant='button' />
          )}
        </form>
        <form onSubmit={handleValidateSku}>
          <GenericInputAction
            name='sku'
            id='sku'
            placeholder='Escanea SKU/EAN/DUN'
            value={skuValue}
            onChange={(e) => {
              setSkuValue(e.target.value)
              setSkuStatus('default')
            }}
            status={skuStatus}
            error={skuStatus === 'error' && !isPositionAttributeMessage(skuError)}
            errorText={isPositionAttributeMessage(skuError) ? '' : skuError}
            disabled={positionStatus !== 'success'}
            inputRef={skuInputRef}
            adornment={
              skuStatus === 'default' ? (
                <ScanIcon width={28} height={36} />
              ) : (
                <ClearAdornment onClick={handleClearSku} disabled={false} />
              )
            }
          />
          {skuStatus === 'error' && isPositionAttributeMessage(skuError) && (
            <PositionAttributeAlert message={skuError} variant='button' />
          )}
        </form>
        {!unitaryPutaway && (
          <Fragment>
            <GenericInputAction
              type='number'
              name='qty'
              id='qty'
              placeholder={getQtyPlaceholder()}
              value={qtyValue}
              onChange={(e) => {
                setQtyValue(e.target.value)
                setQtyError('')
              }}
              status={qtyError ? 'error' : 'default'}
              error={!!qtyError && !isPositionAttributeMessage(qtyError)}
              errorText={isPositionAttributeMessage(qtyError) ? '' : qtyError}
              disabled={skuStatus !== 'success'}
              inputRef={qtyInputRef}
              onKeyPress={(e) => e.key === 'Enter' && handleAddQty()}
              adornment={<NextAdornment onClick={handleAddQty} />}
            />
            {qtyError && isPositionAttributeMessage(qtyError) && (
              <PositionAttributeAlert message={qtyError} variant='button' />
            )}
            {skuDun && skuDunUnitsPerPack && parseInt(qtyValue) > 0 && (
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', px: 1, pt: '2px' }}
              >
                = {parseInt(qtyValue) * skuDunUnitsPerPack} unidades
              </Typography>
            )}
          </Fragment>
        )}
      </Box>

      {/* BOTONES */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '4px', px: '8px' }}
      >
        {/* Un solo boton para los dos campos: el titulo indica cual se va a leer. */}
        <CameraScanButton
          inputRef={isScanningPosition ? positionInputRef : skuInputRef}
          onScan={handleCameraScan}
          onCancel={handleCameraCancel}
          onError={(msg) => openSnackBar(msg, false)}
          disabled={saving || validatingPosition || validatingSku}
          dialogTitle={
            isScanningPosition ? 'Escanear posición' : 'Escanear SKU'
          }
          helperText={
            isScanningPosition
              ? 'Centra el código de la posición dentro del recuadro'
              : 'Centra el código del producto dentro del recuadro'
          }
        />

        {skuList.length > 0 && (
          <Button
            fullWidth
            variant='contained'
            color='primary'
            disabled={saving}
            onClick={() => handleSave()}
          >
            Guardar y Cambiar ubicación
          </Button>
        )}
        {hasLpn && (
          <Button
            fullWidth
            variant='contained'
            disabled={saving}
            style={{ backgroundColor: '#32CD32', marginTop: '4px' }}
            onClick={() => confirmModal.openModal('sm', lpn)}
          >
            Finalizar ingreso LPN
          </Button>
        )}
      </Box>

      {/* Espacios, tamaños componente, y validar que el ingreso por cantidad desactivado, la pantalla ver detalles, hace lo mismo, muestra lo guardado con mas detalle */}

      {skuList.length > 0 && (
        <Box
          sx={{ px: '8px', display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <OptionToggle
            options={['SKU', 'EAN', 'Nombre']}
            defaultValue='SKU'
            size='small'
            onChange={(val) => setViewMode(val as SkuViewMode)}
          />
          {skuList.map((s) => {
            const label =
              viewMode === 'SKU'
                ? s.sku
                : viewMode === 'EAN'
                  ? s.ean
                  : s.productName
            const packs =
              s.dun && s.dunUnitsPerPack
                ? Math.round(s.qty / s.dunUnitsPerPack)
                : null
            return (
              <Box
                key={`${s.skuId}-${s.batchId ?? 0}-${s.dun ?? ''}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  px: 2,
                  py: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 700,
                      color: s.withExcess ? 'error.main' : 'success.main'
                    }}
                  >
                    {packs !== null
                      ? `${packs}x ${s.dunPackageName ?? 'Pack'} (x${s.qty}) -`
                      : `${s.qty}x`}
                    &nbsp;
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 700,
                      color: s.withExcess ? 'error.main' : 'success.main',
                      flex: 1
                    }}
                  >
                    {label}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveItem(s.skuId, s.batchId, s.dun)}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
                {packs !== null && (
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {s.dunUnitsPerPack} uds/{s.dunPackageName ?? 'Pack'}
                  </Typography>
                )}
                {(s.batchNumber || s.batchExpirationDate) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: 'wrap'
                    }}
                  >
                    {s.batchNumber && (
                      <Chip
                        label={`Lote: ${s.batchNumber}`}
                        size='small'
                        sx={{
                          backgroundColor: '#fff9c4',
                          color: '#f57f17',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                      />
                    )}
                    {s.batchExpirationDate && (
                      <Chip
                        label={`Vence: ${formatExpirationDate(s.batchExpirationDate, 'dd-MM-yyyy')}`}
                        size='small'
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      <Modal
        open={backConfirmModal.isOpen}
        maxWidth={backConfirmModal.size}
        title={backConfirmModal.title}
        onClose={backConfirmModal.closeModal}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography
            variant='h6'
            sx={{ textAlign: 'center', fontWeight: 600 }}
          >
            Al volver la información ingresada se eliminará. ¿Está seguro/a que
            desea eliminarla?
          </Typography>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => {
              backConfirmModal.closeModal()
              prev()
            }}
            sx={{
              borderRadius: '8px',
              py: 1.2,
              textTransform: 'uppercase',
              fontWeight: 600
            }}
          >
            Sí, deseo eliminarla
          </Button>
          <Button
            fullWidth
            variant='outlined'
            onClick={backConfirmModal.closeModal}
            sx={{
              borderRadius: '8px',
              py: 1.2,
              textTransform: 'uppercase',
              fontWeight: 600
            }}
          >
            No, deseo permanecer aquí
          </Button>
        </Box>
      </Modal>

      <Modal
        open={batchModal.isOpen}
        maxWidth={batchModal.size}
        title={batchModal.title}
        disableEscapeKeyDown
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            fullWidth
            inputRef={batchSearchRef}
            variant='outlined'
            size='small'
            placeholder='Escanear lote o fecha...'
            value={batchSearch}
            onChange={(e) => handleBatchSearchChange(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />

          <CameraScanButton
            inputRef={batchSearchRef}
            onScan={handleBatchCameraScan}
            onCancel={() =>
              autoFocus && setTimeout(() => batchSearchRef.current?.focus(), 0)
            }
            onError={(msg) => openSnackBar(msg, false)}
            disabled={loadingBatches}
            dialogTitle='Escanear lote'
            helperText='Centra el código del lote dentro del recuadro'
          />

          <Button
            fullWidth
            variant='outlined'
            color='secondary'
            onClick={handleShowAllBatches}
            disabled={loadingBatches}
            sx={{ borderRadius: '8px', textTransform: 'uppercase' }}
          >
            Mostrar todos los lotes
          </Button>
          <BatchTable
            batches={paginatedBatches}
            showBatchNumber
            showExpirationDate
            isLoading={loadingBatches}
            onBatchClick={handleBatchSelect}
            emptyText='No se encontraron lotes'
          />
          <Pagination
            pagination={{
              page: batchPagination.page,
              pageSize: batchPagination.pageSize,
              totalItem: filteredBatches.length
            }}
            onPaginationChange={handleBatchPaginationChange}
          />
        </Box>
      </Modal>

      <Modal
        open={qualityModal.isOpen}
        maxWidth={qualityModal.size}
        title={qualityModal.title}
        onClose={qualityModal.closeModal}
        cancelButton={{
          action: () => {
            qualityModal.closeModal()
            handleClearPosition()
          },
          title: 'Cancelar'
        }}
        successButton={{
          action: () => {
            qualityModal.closeModal()
            if (autoFocus) setTimeout(() => skuInputRef.current?.focus(), 50)
          },
          title: 'Aceptar'
        }}
      >
        <Typography variant='body1' style={{ color: 'red' }}>
          Has ingresado una ubicación de calidad, ¿Deseas ingresar productos en
          esta ubicación?
        </Typography>
      </Modal>

      {/* Detalle de las Asn relacionadas, a demanda desde el aviso del header */}
      <Modal
        open={relatedAsnModal.isOpen}
        maxWidth={relatedAsnModal.size}
        customTitle={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 20 }} />
            <Typography variant='h6'>ASN relacionada detectada</Typography>
          </Box>
        }
        onClose={relatedAsnModal.closeModal}
        successButton={{
          action: relatedAsnModal.closeModal,
          title: 'Entendido'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2'>
            Para el ingreso de la devolución
            {relatedAsnOrderId ? (
              <Fragment>
                {' '}
                asociada al pedido <strong>{relatedAsnOrderId}</strong>
              </Fragment>
            ) : (
              ''
            )}
            , se crearon {totalAsnCount} ASN por tener productos internos y
            externos.
          </Typography>
          {singleRelatedAsn ? (
            <Typography variant='body2'>
              La otra ASN, <strong>{singleRelatedAsn.asnNumber}</strong>, está{' '}
              <strong>{singleRelatedAsn.asnStateName}</strong>. Verifica que
              ambas se completen para finalizar la devolución.
            </Typography>
          ) : (
            <Fragment>
              <Typography variant='body2'>Las otras ASN son:</Typography>
              {relatedAsns.map((related) => (
                <RelatedAsnRow key={related.asnId} relatedAsn={related} />
              ))}
              <Typography variant='body2'>
                Verifica que todas se completen para finalizar la devolución.
              </Typography>
            </Fragment>
          )}
        </Box>
      </Modal>

      {/* Cierre de la ubicación en una devolución mixta */}
      <Modal
        open={finishRelatedAsnModal.isOpen}
        maxWidth={finishRelatedAsnModal.size}
        title={finishRelatedAsnModal.title}
        onClose={() => {
          finishRelatedAsnModal.closeModal()
          goToStep(eStepAsnLocate.ScanStep)
        }}
        successButton={{
          action: () => {
            finishRelatedAsnModal.closeModal()
            goToStep(eStepAsnLocate.ScanStep)
          },
          title: 'Entendido'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircleOutlineIcon
              sx={{ color: 'success.main', fontSize: 20 }}
            />
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              ASN {workOrder?.asnNumber} finalizada
            </Typography>
          </Box>
          {pendingRelatedAsns.length === 0 ? (
            <Typography variant='body2'>
              Devolución completa: todas las ASN relacionadas ya están
              almacenadas.
            </Typography>
          ) : (
            <Fragment>
              <Typography variant='body2'>
                Esta devolución tiene ASN relacionadas que aún deben
                almacenarse:
              </Typography>
              {pendingRelatedAsns.map((related) => (
                <RelatedAsnRow key={related.asnId} relatedAsn={related} />
              ))}
            </Fragment>
          )}
        </Box>
      </Modal>

      <Modal
        open={confirmModal.isOpen}
        maxWidth={confirmModal.size}
        title={confirmModal.title}
        onClose={confirmModal.closeModal}
        loading={saving}
        successButton={{
          action: () => {
            confirmModal.closeModal()
            handleFinalizeLpn()
          },
          title: 'Confirmar'
        }}
        cancelButton={{ action: confirmModal.closeModal, title: 'Cancelar' }}
      >
        <span>
          ¿Desea finalizar la LPN: <strong>{lpn}</strong>?
          {pendingQty > 0 && (
            <>
              <br />
              Aún restan <strong>{pendingQty}</strong> unidades por ingresar.
            </>
          )}
        </span>
      </Modal>

      <Modal
        open={!!completedBatchInfo}
        title='Lote Completado'
        maxWidth='sm'
        onClose={() => {
          setCompletedBatchInfo(null)
          autoFocus && setTimeout(() => skuInputRef.current?.focus(), 100)
        }}
        successButton={{
          action: () => {
            setCompletedBatchInfo(null)
            autoFocus && setTimeout(() => skuInputRef.current?.focus(), 100)
          },
          title: 'Continuar'
        }}
      >
        <Typography variant='body1' sx={{ mb: 1 }}>
          ¡Has completado el total de unidades pactadas para este lote!
        </Typography>
        <Typography
          variant='body2'
          sx={{ fontWeight: 'bold', color: '#2196f3' }}
        >
          {completedBatchInfo}
        </Typography>
        <Typography variant='body2' sx={{ mt: 2, color: '#666' }}>
          Puedes continuar ubicando productos para otros lotes
        </Typography>
      </Modal>

      <Modal
        open={!!pendingExcess}
        title='Sobrante detectado'
        maxWidth='sm'
        onClose={handleCancelExcess}
        successButton={{ action: handleConfirmExcess, title: 'Confirmar' }}
        cancelButton={{ action: handleCancelExcess, title: 'Cancelar' }}
      >
        {pendingExcess && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant='body1'>
              La cantidad para este SKU ya ha sido completada.
            </Typography>

            <Typography
              variant='body2'
              sx={{ fontWeight: 'bold', color: '#2196f3' }}
            >
              SKU: {pendingExcess.sku}
            </Typography>

            {pendingExcess.dun && pendingExcess.dunUnitsPerPack && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Escaneaste {pendingExcess.qty} unidades (
                {pendingExcess.qty / pendingExcess.dunUnitsPerPack}x{' '}
                {pendingExcess.dunPackageName ?? 'Pack'}).
              </Typography>
            )}

            <Typography variant='body1'>
              Si deseas ingresar esta unidad, se registrará como sobrante.
            </Typography>

            <Typography variant='body1'>¿Deseas continuar?</Typography>
          </Box>
        )}
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Box>
  )
}
